import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface PageActionBarProps {
  /** Action buttons (rendered at the end). */
  children: ReactNode;
  /** Optional content rendered at the start (hints, status text, etc.). */
  start?: ReactNode;
  /** Override or extend the wrapper classes. */
  className?: string;
}

/**
 * Sticky action bar for page-level actions (Save, Discard, etc.).
 *
 * Uses `position: sticky` (so it floats above content while scrolling) plus
 * `mt-auto` (so it pins to the bottom of short pages). Never overlaps the
 * global footer (`AppVersion`) or any other layout chrome.
 *
 * Requirement: the page wrapper must be a flex column that fills its parent —
 * e.g. `<div className='flex flex-1 flex-col gap-6'>...</div>`. Without this,
 * `mt-auto` has no effect and the bar will sit at the natural end of content
 * instead of the bottom of the viewport.
 *
 * Negative horizontal margins match the dashboard layout's `p-4 md:px-6` page
 * padding so the bar visually spans the full content width. If your page uses
 * different padding, override via `className`.
 */
export function PageActionBar({
  children,
  start,
  className
}: PageActionBarProps) {
  return (
    <div
      className={cn(
        'bg-background/95 supports-[backdrop-filter]:bg-background/85 sticky bottom-0 z-10 -mx-4 mt-auto flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 backdrop-blur md:-mx-6 md:px-6',
        className
      )}
    >
      <div className='min-w-0 flex-1'>{start}</div>
      <div className='flex items-center gap-2'>{children}</div>
    </div>
  );
}
